// src/supabase/oura.ts
// Frontend client wrappers for Oura Ring edge functions.
// Tokens (access_token, refresh_token) are never exposed to the client —
// all writes to sensitive columns happen server-side via edge functions.

import { Browser } from "@capacitor/browser";
import { supabase } from "./client";
import { isNative } from "../lib/platform";

export type OuraConnection = {
  enabled: boolean;
  authoritative: boolean;
  lastSyncAt: string | null;
  lastError: string | null;
};

const SESSION_EXPIRED_ERROR = "Session expired, please sign in again.";

/**
 * Resolves a validated auth state for the current user.
 * Uses refresh when possible and verifies identity via getUser().
 */
async function getValidatedAuth() {
  const {
    data: { session },
    error: sessionError,
  } = await supabase.auth.getSession();

  if (sessionError || !session?.refresh_token) {
    throw new Error(SESSION_EXPIRED_ERROR);
  }

  const { data: refreshedData, error: refreshError } = await supabase.auth.refreshSession({
    refresh_token: session.refresh_token,
  });

  const refreshedSession = refreshedData.session;
  if (refreshError || !refreshedSession?.access_token) {
    throw new Error(SESSION_EXPIRED_ERROR);
  }

  const { data: userData, error: userError } = await supabase.auth.getUser(
    refreshedSession.access_token,
  );

  const user = userData.user;
  if (userError || !user?.id) {
    throw new Error(SESSION_EXPIRED_ERROR);
  }

  return { session: refreshedSession, user };
}

/**
 * Returns a non-expired access token, refreshing when close to expiry.
 */
async function getFreshToken(): Promise<string> {
  const {
    data: { session: existingSession },
  } = await supabase.auth.getSession();

  if (!existingSession) {
    throw new Error("Not signed in");
  }

  let session = existingSession;
  const expiresAt = (session.expires_at ?? 0) * 1000;

  if (expiresAt - Date.now() < 60_000) {
    const { data, error } = await supabase.auth.refreshSession();
    if (error || !data.session) {
      throw new Error(SESSION_EXPIRED_ERROR);
    }
    session = data.session;
  }

  return session.access_token;
}

async function callOuraEdgeFunction(
  path: string,
  body?: Record<string, unknown>,
): Promise<Response> {
  const token = await getFreshToken();

  const res = await fetch(`${import.meta.env.VITE_SUPABASE_URL}/functions/v1/${path}`, {
    method: "POST",
    headers: {
      Authorization: `Bearer ${token}`,
      ...(body ? { "Content-Type": "application/json" } : {}),
    },
    ...(body ? { body: JSON.stringify(body) } : {}),
  });

  if (!res.ok) {
    if (res.status === 401 || res.status === 403) {
      throw new Error(SESSION_EXPIRED_ERROR);
    }
    throw new Error(await res.text());
  }

  return res;
}

/**
 * Fetches the Oura OAuth start URL from the edge function and opens Oura's
 * consent page.
 *
 * - Native: opens in Capacitor's in-app browser (SFSafariViewController on iOS)
 *   and passes the iOS custom URL scheme as the post-auth redirect target.
 *   The app_redirect is stored in the CSRF state row; oura-oauth-callback
 *   uses it to redirect back via deep link when done.
 *
 * - Web: navigates the current page to Oura's consent URL (existing behavior).
 */
export async function startOuraConnect(): Promise<void> {
  if (isNative) {
    // Tell the edge function to redirect back to our custom scheme after auth
    const res = await callOuraEdgeFunction("oura-oauth-start", {
      appRedirect: "com.weightplanner.app://",
    });
    const { url } = await res.json();
    // Open in SFSafariViewController — keeps the user inside the app
    await Browser.open({ url, presentationStyle: "popover" });
    // The oura-oauth-callback edge function will redirect to
    // com.weightplanner.app://?tab=settings&oura=connected, which fires
    // the Capacitor App 'appUrlOpen' event handled in App.tsx.
    // Browser will close automatically when the custom scheme is triggered.
  } else {
    const res = await callOuraEdgeFunction("oura-oauth-start");
    const { url } = await res.json();
    window.location.href = url;
  }
}

/**
 * Calls the oura-disconnect edge function which clears tokens and sets
 * enabled=false. The connection row is retained for history.
 */
export async function disconnectOura(): Promise<void> {
  await callOuraEdgeFunction("oura-disconnect");
}

/**
 * Triggers a manual Oura sync for the current user (last 7 days).
 * Returns a summary of how many entries were synced vs skipped.
 */
export async function syncOuraNow(): Promise<{ synced: number; skipped: number }> {
  const res = await callOuraEdgeFunction("oura-sync");
  return res.json();
}

/**
 * Reads the current user's Oura connection row.
 * Tokens are NOT included in the select — they stay server-side.
 * Returns null if no connection exists.
 */
export async function getOuraConnection(): Promise<OuraConnection | null> {
  const { data, error } = await supabase
    .from("oura_connections")
    .select("enabled, authoritative, last_sync_at, last_error")
    .maybeSingle();

  if (error || !data) return null;

  return {
    enabled: data.enabled,
    authoritative: data.authoritative,
    lastSyncAt: data.last_sync_at ?? null,
    lastError: data.last_error ?? null,
  };
}

/**
 * Updates the enabled and/or authoritative flags on the user's oura_connections row.
 * RLS allows users to update their own non-token fields directly.
 */
export async function updateOuraSettings(
  patch: Partial<Pick<OuraConnection, "enabled" | "authoritative">>,
): Promise<void> {
  const { user } = await getValidatedAuth();
  const userId = user.id;

  const { error } = await supabase
    .from("oura_connections")
    .update({ ...patch, updated_at: new Date().toISOString() })
    .eq("user_id", userId);

  if (error) throw new Error(error.message);
}
